import { AxiosError } from 'axios';

import { useMutation, useQuery } from '@tanstack/react-query';
import { Address } from 'viem';

import { get, post } from '@helpers/common/axios.helper';
import { parseAxiosError } from '@helpers/common/error.helpers';

import showToast from '@components/common/Toast';

import {
  API_BOND_INFO,
  API_BOND_SIGN_MINT,
  API_BOND_SIGN_REDEEM,
  API_BOND_TOTAL_REWARDS_DISTRIBUTED,
} from '@constants/bonds/api.constants';
import { ToastType } from '@constants/common/toast.constants';

import {
  BondsInfoDto,
  BondsTotalRewardsDistributedDto,
  SignMintDto,
  SignRedeemDto,
} from '@dto/bonds/bonds.dto';

const getBondsInfo = (): Promise<BondsInfoDto> =>
  get<BondsInfoDto>(API_BOND_INFO);

export const useBondsInfoRequest = () =>
  useQuery<BondsInfoDto>({
    queryKey: [API_BOND_INFO],
    queryFn: () => getBondsInfo(),
    retry: false,
    refetchInterval: 60000,
  });

const signMint = (params: {
  signature: string;
  data: {
    account: Address;
    nftsAmount: number;
    timestamp: number;
  };
}): Promise<SignMintDto> => post<SignMintDto>(API_BOND_SIGN_MINT, params);

export const useSignMint = () =>
  useMutation({
    mutationFn: signMint,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
  });

const signRedeem = (params: {
  signature: string;
  data: {
    account: Address;
    nftsAmount: number;
    timestamp: number;
  };
}): Promise<SignRedeemDto> => post<SignRedeemDto>(API_BOND_SIGN_REDEEM, params);

export const useSignRedeem = () =>
  useMutation({
    mutationFn: signRedeem,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
  });

const getBondsTotalRewardsDistributed =
  (): Promise<BondsTotalRewardsDistributedDto> =>
    get<BondsTotalRewardsDistributedDto>(API_BOND_TOTAL_REWARDS_DISTRIBUTED);

export const useBondsTotalRewardsDistributed = () =>
  useQuery<BondsTotalRewardsDistributedDto>({
    queryKey: [API_BOND_TOTAL_REWARDS_DISTRIBUTED],
    queryFn: () => getBondsTotalRewardsDistributed(),
    retry: false,
    refetchInterval: 60000,
  });
